import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, LogOut, Loader2, ArrowLeft, Megaphone, Trophy, Handshake, Tag, Users, MessageSquareQuote, Mail, Trash2, Settings, LayoutGrid, Star } from "lucide-react";
import { adminLogin, verifyAdmin, getToken, clearToken, fetchAll, deleteItem, resolveImg } from "../adminApi";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import CrudModule from "../admin/CrudModule";
import ShareButtons from "../ShareButtons";
import { SettingsPanel, CourtsPanel, FeaturedPartnerPanel } from "../admin/SettingsPanels";
import Logo from "../../components/Logo";

/* ---------------- Login ---------------- */
function Login({ onDone }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try { await adminLogin(password); onDone(); }
    catch { setErr("Incorrect password. Try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen d2-mesh d2-grain flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-md bg-[#0b241a] border border-white/10 rounded-2xl p-8 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <Logo size={44} />
          <div>
            <h1 className="font-display uppercase text-2xl text-white leading-none">Admin</h1>
            <p className="font-grotesk text-xs text-white/50 uppercase tracking-widest">Content Manager</p>
          </div>
        </div>
        <label className="lbl">Password</label>
        <div className="relative">
          <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter admin password" autoFocus
            className="w-full bg-[#06170f] border border-white/15 focus:border-sc-lime outline-none rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-white/40 font-grotesk" />
        </div>
        {err && <p className="text-red-400 text-sm mt-3 font-grotesk">{err}</p>}
        <button type="submit" disabled={loading} className="d2-btn w-full py-3 mt-6 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Log In
        </button>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-white/50 hover:text-sc-lime text-sm font-grotesk"><ArrowLeft className="w-4 h-4" /> Back to site</Link>
        <StyleTag />
      </form>
    </div>
  );
}

/* ---------------- Field schemas ---------------- */
const ANN_FIELDS = [
  { key: "type", label: "Type", type: "select", options: [
    { value: "schedule", label: "Schedule" }, { value: "event", label: "Event / Tournament" },
    { value: "sponsor", label: "Partner / Sponsor" }, { value: "general", label: "General News" },
  ]},
  { key: "text", label: "Text line", type: "textarea", max: 150, placeholder: "e.g. OPEN PLAY tonight 6-9PM" },
  { key: "logo", label: "Logo (optional)", type: "image" },
  { key: "link", label: "Link (optional)", type: "text", placeholder: "https://…" },
  { key: "active", label: "Show on ticker", type: "switch" },
];
const ANN_EMPTY = { type: "general", text: "", logo: null, link: "", active: true };

const EVENT_FIELDS = [
  { key: "title", label: "Title", type: "text", placeholder: "Grand Opening Doubles Shootout" },
  { key: "date", label: "Date", type: "text", placeholder: "Aug 30, 2025 or Every Fri, 6-9pm" },
  { key: "tag", label: "Category", type: "text", placeholder: "Tournament / Open Play / League / Clinic" },
  { key: "level", label: "Skill Level", type: "text", placeholder: "All Levels / 3.0 - 4.5" },
  { key: "image", label: "Image", type: "image" },
  { key: "active", label: "Show on site", type: "switch" },
];
const EVENT_EMPTY = { title: "", date: "", tag: "", level: "", image: null, active: true };

const PARTNER_FIELDS = [
  { key: "name", label: "Name", type: "text", placeholder: "Selkirk" },
  { key: "type", label: "Category", type: "text", placeholder: "Equipment / Rating Partner / Hydration" },
  { key: "logo", label: "Logo (optional)", type: "image" },
  { key: "link", label: "Link (optional)", type: "text", placeholder: "https://…" },
  { key: "active", label: "Show on site", type: "switch" },
];
const PARTNER_EMPTY = { name: "", type: "", logo: null, link: "", active: true };

const RATE_FIELDS = [
  { key: "name", label: "Name", type: "text", placeholder: "Court Rental" },
  { key: "price", label: "Price", type: "text", placeholder: "₱700" },
  { key: "unit", label: "Unit", type: "text", placeholder: "/ hour" },
  { key: "features", label: "Features", type: "tags", placeholder: "Indoor pickleball court" },
  { key: "cta", label: "Button Label", type: "text", placeholder: "Reserve" },
  { key: "highlight", label: "Highlight as popular", type: "switch" },
  { key: "active", label: "Show on site", type: "switch" },
];
const RATE_EMPTY = { name: "", price: "", unit: "", features: [], cta: "Reserve", highlight: false, active: true };

const COACH_FIELDS = [
  { key: "name", label: "Name", type: "text", placeholder: "Coach Marco Reyes" },
  { key: "role", label: "Role / Title", type: "text", placeholder: "Head Pickleball Coach" },
  { key: "bio", label: "Short Bio", type: "textarea", max: 220, placeholder: "DUPR 5.0 · specializes in doubles strategy…" },
  { key: "photo", label: "Photo", type: "image" },
  { key: "active", label: "Show on site", type: "switch" },
];
const COACH_EMPTY = { name: "", role: "", bio: "", photo: null, active: true };

const TESTI_FIELDS = [
  { key: "name", label: "Name", type: "text", placeholder: "Andrea L." },
  { key: "role", label: "Role / Tagline", type: "text", placeholder: "3.5 player" },
  { key: "quote", label: "Quote", type: "textarea", max: 260, placeholder: "The best courts in the metro…" },
  { key: "rating", label: "Rating (1-5)", type: "number", placeholder: "5" },
  { key: "video_url", label: "Video URL (optional)", type: "text", placeholder: "https://youtu.be/… or https://youtube.com/watch?v=…" },
  { key: "photo", label: "Photo (optional)", type: "image" },
  { key: "active", label: "Show on site", type: "switch" },
];
const TESTI_EMPTY = { name: "", role: "", quote: "", rating: 5, video_url: "", photo: null, active: true };

/* ---------------- Row renderers ---------------- */
const annRow = (a) => (
  <>
    {a.logo ? <img src={resolveImg(a.logo)} alt="" className="h-10 w-14 object-contain bg-white/5 rounded shrink-0" />
      : <span className="h-10 w-14 rounded bg-white/5 flex items-center justify-center shrink-0 font-grotesk uppercase text-[9px] tracking-wider text-sc-lime">{a.type}</span>}
    <div className="min-w-0"><p className="font-grotesk text-white/90 truncate">{a.text}</p>
      <p className="font-grotesk text-[11px] uppercase tracking-widest text-white/40 mt-0.5">{a.type}{a.link ? " · linked" : ""}</p></div>
  </>
);
const eventRow = (e) => (
  <>
    {e.image ? <img src={resolveImg(e.image)} alt="" className="h-12 w-16 object-cover rounded shrink-0" />
      : <span className="h-12 w-16 rounded bg-white/5 shrink-0" />}
    <div className="min-w-0"><p className="font-grotesk text-white/90 truncate">{e.title}</p>
      <p className="font-grotesk text-[11px] uppercase tracking-widest text-white/40 mt-0.5">{e.tag} · {e.date} · {e.level}</p></div>
  </>
);
const partnerRow = (p) => (
  <>
    {p.logo ? <img src={resolveImg(p.logo)} alt="" className="h-10 w-14 object-contain bg-white/5 rounded shrink-0" />
      : <span className="h-10 w-14 rounded bg-sc-lime/10 flex items-center justify-center shrink-0 font-display text-lg text-sc-lime">{(p.name || "?")[0]}</span>}
    <div className="min-w-0"><p className="font-grotesk text-white/90 truncate">{p.name}</p>
      <p className="font-grotesk text-[11px] uppercase tracking-widest text-white/40 mt-0.5">{p.type}</p></div>
  </>
);
const rateRow = (r) => (
  <>
    <span className="h-12 w-16 rounded bg-white/5 flex flex-col items-center justify-center shrink-0">
      <span className="font-display text-lg text-sc-lime leading-none">{r.price}</span>
      <span className="font-mono2 text-[8px] text-white/40">{r.unit}</span>
    </span>
    <div className="min-w-0"><p className="font-grotesk text-white/90 truncate">{r.name}{r.highlight && <span className="ml-2 text-[10px] uppercase tracking-widest text-sc-lime">★ popular</span>}</p>
      <p className="font-grotesk text-[11px] text-white/40 mt-0.5 truncate">{(r.features || []).length} features · "{r.cta}"</p></div>
  </>
);
const coachRow = (c) => (
  <>
    {c.photo ? <img src={resolveImg(c.photo)} alt="" className="h-12 w-12 object-cover rounded-full shrink-0" />
      : <span className="h-12 w-12 rounded-full bg-sc-lime/10 flex items-center justify-center shrink-0 font-display text-lg text-sc-lime">{(c.name || "?")[0]}</span>}
    <div className="min-w-0"><p className="font-grotesk text-white/90 truncate">{c.name}</p>
      <p className="font-grotesk text-[11px] uppercase tracking-widest text-white/40 mt-0.5 truncate">{c.role}</p></div>
  </>
);
const testiRow = (t) => (
  <>
    {t.photo ? <img src={resolveImg(t.photo)} alt="" className="h-12 w-12 object-cover rounded-full shrink-0" />
      : <span className="h-12 w-12 rounded-full bg-sc-lime/10 flex items-center justify-center shrink-0 font-display text-lg text-sc-lime">{(t.name || "?")[0]}</span>}
    <div className="min-w-0"><p className="font-grotesk text-white/90 truncate">{t.name} <span className="text-sc-lime text-xs">{"★".repeat(Math.max(0, Math.min(5, t.rating || 5)))}</span>{t.video_url ? <span className="ml-2 text-[10px] uppercase tracking-widest text-sc-lime/80 bg-sc-lime/10 px-2 py-0.5 rounded-full">▶ Video</span> : null}</p>
      <p className="font-grotesk text-[11px] text-white/40 mt-0.5 truncate">"{t.quote}"</p></div>
  </>
);

function StyleTag() {
  return (
    <style>{`
      .lbl { display:block; font-family:'Space Grotesk',sans-serif; text-transform:uppercase; letter-spacing:.12em; font-size:11px; color:rgba(200,240,0,.85); margin-bottom:8px; }
      .adm-input { width:100%; background:#06170f; border:1px solid rgba(255,255,255,.15); color:#eafbe6; padding:11px 13px; border-radius:10px; outline:none; font-family:'Space Grotesk',sans-serif; }
      .adm-input:focus { border-color: var(--sc-lime); }
      .adm-input::placeholder { color: rgba(234,251,230,.4); }
    `}</style>
  );
}

/* ---------------- Subscribers (read-only) ---------------- */
function SubscribersPanel({ onLogout }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = React.useCallback(async () => {
    setLoading(true);
    try { setSubs(await fetchAll("subscribers")); }
    catch { onLogout && onLogout(); }
    finally { setLoading(false); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const remove = async (s) => { await deleteItem("subscribers", s.id); load(); };
  const exportCsv = () => {
    const rows = [["email", "date"], ...subs.map((s) => [s.email, (s.created_at || "").slice(0, 10)])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "supreme-courts-subscribers.csv";
    a.click();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display uppercase text-2xl text-white flex items-center gap-2"><Mail className="w-5 h-5 text-sc-lime" /> Newsletter Subscribers ({subs.length})</h2>
        {subs.length > 0 && <button onClick={exportCsv} className="d2-btn-ghost px-5 py-2 text-xs">Export CSV</button>}
      </div>
      {loading ? (
        <div className="text-white/50 flex items-center gap-2 font-grotesk"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : subs.length === 0 ? (
        <div className="text-white/50 font-grotesk bg-[#0b241a] border border-white/10 rounded-2xl p-8 text-center">No subscribers yet. They'll appear here when visitors sign up in the footer.</div>
      ) : (
        <div className="bg-[#0b241a] border border-white/10 rounded-2xl divide-y divide-white/5">
          {subs.map((s) => (
            <div key={s.id} className="flex items-center gap-4 p-4">
              <Mail className="w-4 h-4 text-sc-lime shrink-0" />
              <span className="font-grotesk text-white/90 flex-1 truncate">{s.email}</span>
              <span className="font-mono2 text-[11px] text-white/40">{(s.created_at || "").slice(0, 10)}</span>
              <button onClick={() => remove(s)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard({ onLogout }) {
  const logout = () => { clearToken(); onLogout(); };
  return (
    <div className="min-h-screen bg-[#06170f]">
      <header className="border-b border-white/10 sticky top-0 bg-[#06170f]/90 backdrop-blur z-20">
        <div className="max-w-[1200px] mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div>
              <h1 className="font-display uppercase text-xl text-white leading-none">Content Manager</h1>
              <p className="font-grotesk text-[11px] text-white/50 uppercase tracking-widest">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="d2-btn-ghost px-4 py-2 text-xs">View Site</Link>
            <button onClick={logout} className="text-white/60 hover:text-sc-lime inline-flex items-center gap-1.5 font-grotesk text-sm"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-5 py-8">
        <Tabs defaultValue="settings">
          <TabsList className="bg-[#0b241a] border border-white/10 mb-8 flex-wrap h-auto">
            <TabsTrigger value="settings" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><Settings className="w-4 h-4" /> Site Settings</TabsTrigger>
            <TabsTrigger value="courts" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><LayoutGrid className="w-4 h-4" /> Courts</TabsTrigger>
            <TabsTrigger value="featured" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><Star className="w-4 h-4" /> Featured Partner</TabsTrigger>
            <TabsTrigger value="announcements" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><Megaphone className="w-4 h-4" /> Announcements</TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><Trophy className="w-4 h-4" /> Events</TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><Handshake className="w-4 h-4" /> Partners</TabsTrigger>
            <TabsTrigger value="rates" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><Tag className="w-4 h-4" /> Rates & Fees</TabsTrigger>
            <TabsTrigger value="coaches" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><Users className="w-4 h-4" /> Coaches</TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><MessageSquareQuote className="w-4 h-4" /> Testimonials</TabsTrigger>
            <TabsTrigger value="subscribers" className="data-[state=active]:bg-sc-lime data-[state=active]:text-[#06170f] text-white/70 font-grotesk uppercase text-xs tracking-wide gap-2"><Mail className="w-4 h-4" /> Subscribers</TabsTrigger>
          </TabsList>

          <TabsContent value="settings">
            <SettingsPanel onLogout={logout} />
          </TabsContent>
          <TabsContent value="courts">
            <CourtsPanel onLogout={logout} />
          </TabsContent>
          <TabsContent value="featured">
            <FeaturedPartnerPanel onLogout={logout} />
          </TabsContent>
          <TabsContent value="announcements">
            <CrudModule resource="announcements" title="Announcement" fields={ANN_FIELDS} emptyItem={ANN_EMPTY} renderRow={annRow} onLogout={logout} />
          </TabsContent>
          <TabsContent value="events">
            <CrudModule resource="events" title="Event" fields={EVENT_FIELDS} emptyItem={EVENT_EMPTY} renderRow={eventRow} renderActions={(item) => <ShareButtons event={item} size="sm" />} onLogout={logout} />
          </TabsContent>
          <TabsContent value="partners">
            <CrudModule resource="partners" title="Partner" fields={PARTNER_FIELDS} emptyItem={PARTNER_EMPTY} renderRow={partnerRow} onLogout={logout} />
          </TabsContent>
          <TabsContent value="rates">
            <CrudModule resource="rates" title="Rate" fields={RATE_FIELDS} emptyItem={RATE_EMPTY} renderRow={rateRow} onLogout={logout} />
          </TabsContent>
          <TabsContent value="coaches">
            <CrudModule resource="coaches" title="Coach" fields={COACH_FIELDS} emptyItem={COACH_EMPTY} renderRow={coachRow} onLogout={logout} />
          </TabsContent>
          <TabsContent value="testimonials">
            <CrudModule resource="testimonials" title="Testimonial" fields={TESTI_FIELDS} emptyItem={TESTI_EMPTY} renderRow={testiRow} onLogout={logout} />
          </TabsContent>
          <TabsContent value="subscribers">
            <SubscribersPanel onLogout={logout} />
          </TabsContent>
        </Tabs>
      </div>
      <StyleTag />
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    verifyAdmin().then(() => setAuthed(true)).catch(() => clearToken()).finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="min-h-screen bg-[#06170f] flex items-center justify-center"><Loader2 className="w-6 h-6 text-sc-lime animate-spin" /></div>;
  return authed ? <Dashboard onLogout={() => setAuthed(false)} /> : <Login onDone={() => setAuthed(true)} />;
}
